import { Editor } from '@tinymce/tinymce-react';
import React, { useRef, useEffect, useState } from "react";
import './RichTextEditor.css';
import { loadTranslation } from './help/index.js';
import { Loader } from '../loader/loader.jsx';

const TINYMCE_SRC = '/tinymce/tinymce.min.js';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

const RichTextEditor = ({
  value,
  onChange,
  height = 500,
  placeholder = 'Type your content here...',
  disabled = false,
  menubar = true,
  plugins = 'code preview importcss searchreplace autolink autosave save directionality visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help emoticons quickbars',
  toolbar = 'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media pageembed template link anchor codesample | a11ycheck ltr rtl | showcomments addcomment',
  // async (file: File) => url; enables the image upload tab and pasted/dropped images
  imageUploadHandler,
  configOverrides = {},
}) => {
  const editorRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // TinyMCE calls setContent (cursor jumps to the top) whenever initialValue changes, so only
  // pass on a new value when it did not come from the editor itself, e.g. a controlled parent echoing onChange.
  const lastEmitted = useRef(value);
  const initialValue = useRef(value);
  if (value !== lastEmitted.current) {
    lastEmitted.current = value;
    initialValue.current = value;
  }

  useEffect(() => {
    loadScript(TINYMCE_SRC)
      .then(() => {
        setTimeout(() => {
          setScriptLoaded(true)
        }, 500);
      })
      .catch(() => setScriptLoaded(false));
  }, []);

  // Handle editor content change
  const handleEditorChange = (content) => {
    lastEmitted.current = content;
    if (onChange) {
      onChange(content);
    }
  };

  const config = {
    language: localStorage.language || 'nl',
    height,
    menubar,
    plugins,
    toolbar,
    placeholder,
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
    help_tabs: [
      'shortcuts',
      'iframe_help',
      'template_tags',
    ],
    license_key: 'gpl',
    ...(imageUploadHandler && {
      automatic_uploads: true,
      images_file_types: 'jpg,jpeg,png,gif',
      images_upload_handler: (blobInfo) => imageUploadHandler(blobInfo.blob(), blobInfo.filename()),
    }),
    ...configOverrides,
  }

  function onInit(_evt, editor) {
    editorRef.current = editor
    handleTranslation(editor)
  }

  async function handleTranslation(editor) {
    // help tabs can only be added when the help plugin is loaded
    if (!editor.plugins?.help) return

    // https://www.tiny.cloud/docs/tinymce/latest/help/
    const onInit = await loadTranslation(localStorage.language || 'nl')
    onInit(editor)
  }

  if (!scriptLoaded) {
    return <Loader />;
  }

  return (
    <Editor
      tinymceScriptSrc={TINYMCE_SRC}
      initialValue={initialValue.current}
      onEditorChange={handleEditorChange}
      onInit={onInit}
      init={config}
      disabled={disabled}
    />
  );
};

export default RichTextEditor;